import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Train, GraduationCap, Sparkles, TrendingUp, Home, ArrowRight, ChevronLeft, Check, PoundSterling, Megaphone, Loader2, Send } from "lucide-react";
import Seo from "../components/Seo";
import { getAreaGuide, AREA_GUIDES } from "../data/areaGuides";
import { getAreaSoldPrices, submitValuation, generateAreaCampaign } from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const money = (n) => (n ? `£${Number(n).toLocaleString()}` : "—");

const AreaGuideDetail = () => {
  const { slug } = useParams();
  const area = getAreaGuide(slug);
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user && (user.role === "admin" || user.role === "agent");

  const [sold, setSold] = useState(null);
  const [valForm, setValForm] = useState({ propertyType: "Terraced House", bedrooms: 3, bathrooms: 1, constructionEra: "pre_1914", finish: "average", name: "", email: "", phone: "" });
  const [valResult, setValResult] = useState(null);
  const [valBusy, setValBusy] = useState(false);
  const [campBusy, setCampBusy] = useState(false);

  useEffect(() => {
    setSold(null); setValResult(null);
    if (area) getAreaSoldPrices(area.salesPostcode || area.postcode).then(setSold).catch(() => setSold(null));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitVal = async (e) => {
    e.preventDefault();
    if (!area) return;
    setValBusy(true);
    try {
      const r = await submitValuation({ postcode: area.postcode, propertyType: valForm.propertyType, bedrooms: Number(valForm.bedrooms), bathrooms: Number(valForm.bathrooms), constructionEra: valForm.constructionEra, finish: valForm.finish, name: valForm.name, email: valForm.email, phone: valForm.phone, source: `area-guide-${area.slug}` });
      setValResult(r);
      toast({ title: "Estimate ready", description: "We've also sent your details to our team." });
    } catch { toast({ title: "Couldn't generate estimate", description: "Please try again." }); }
    finally { setValBusy(false); }
  };

  const genCampaign = async () => {
    setCampBusy(true);
    try { const r = await generateAreaCampaign(area.name, area.postcode); toast({ title: `${area.name} campaign generated`, description: `${r.count} drafts saved to Marketing → Pending Review.` }); }
    catch { toast({ title: "Couldn't generate campaign" }); }
    finally { setCampBusy(false); }
  };

  if (!area) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-5 text-center bg-[#faf7f2]">
        <h1 className="font-display text-[30px] text-[#2b2b2b] mb-3">Area guide not found</h1>
        <Link to="/area-guides" className="px-7 py-3 rounded-full bg-[#b8935f] text-white text-[14px] font-500">All Area Guides</Link>
      </div>
    );
  }

  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Place",
      name: `${area.name}, ${area.city || "Bath"} ${area.postcode}`,
      description: area.intro,
      address: { "@type": "PostalAddress", addressLocality: area.city || "Bath", postalCode: area.postcode, addressRegion: (area.city === "Bristol" ? "Bristol" : "Somerset"), addressCountry: "GB" },
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Area Guides", item: "https://anadearestates.com/area-guides" },
        { "@type": "ListItem", position: 2, name: area.name, item: `https://anadearestates.com/area-guides/${area.slug}` },
      ],
    },
  ];

  const related = AREA_GUIDES.filter((a) => a.slug !== area.slug && (a.city || "Bath") === (area.city || "Bath")).slice(0, 3);
  const city = area.city || "Bath";

  return (
    <div className="min-h-[70vh] bg-[#faf7f2]" data-testid="area-guide-detail">
      <Seo
        title={`${area.name} Area Guide — Property, Schools & Living in ${city} ${area.postcode}`}
        description={`Thinking of moving to ${area.name}, ${city} (${area.postcode})? Anadear Estates' local guide covers ${area.name} house prices, schools, transport and lifestyle. ${area.tagline}.`}
        path={`/area-guides/${area.slug}`}
        image={area.heroImage}
        type="article"
        jsonLd={jsonLd}
      />

      <div className="relative h-[46vh] min-h-[340px] overflow-hidden">
        <img src={area.heroImage} alt={`${area.name}, ${city} ${area.postcode}`} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-[1080px] mx-auto px-5 md:px-8 pb-9">
            <Link to="/area-guides" className="inline-flex items-center gap-1.5 text-[13px] text-white/80 hover:text-white mb-3" data-testid="back-to-guides"><ChevronLeft size={15} /> All Area Guides</Link>
            <p className="text-[12px] tracking-[0.28em] uppercase text-[#d9b98a] mb-1 flex items-center gap-2"><MapPin size={14} /> {city} {area.postcode}</p>
            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-300">{area.name}</h1>
            <p className="text-white/80 text-base mt-3 max-w-2xl">{area.tagline}</p>
          </div>
        </div>
      </div>

      <div className="max-w-[1080px] mx-auto px-5 md:px-8 py-14 grid lg:grid-cols-[1fr_340px] gap-12">
        <article className="space-y-10">
          <section className="space-y-4">
            <p className="text-[16px] text-[#3a3a3a] leading-[1.8]">{area.intro}</p>
            <p className="text-[16px] text-[#3a3a3a] leading-[1.8]">{area.intro2}</p>
          </section>

          <section>
            <h2 className="font-display text-[22px] font-500 text-[#2b2b2b] mb-4 flex items-center gap-2"><Sparkles size={19} className="text-[#b8935f]" /> Why live in {area.name}?</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {area.highlights.map((h) => (
                <div key={h} className="flex items-start gap-2.5 p-4 rounded-xl bg-white border border-[#f0ece5]">
                  <Check size={16} className="text-[#b8935f] mt-0.5 shrink-0" />
                  <span className="text-[14px] text-[#4a4a4a]">{h}</span>
                </div>
              ))}
            </div>
            <p className="text-[15px] text-[#6a6a6a] mt-4 italic">{area.vibe}</p>
          </section>

          <section className="grid sm:grid-cols-2 gap-6">
            <div className="p-5 rounded-2xl bg-white border border-[#f0ece5]">
              <h3 className="font-display text-[17px] font-500 text-[#2b2b2b] mb-2 flex items-center gap-2"><Train size={17} className="text-[#b8935f]" /> Getting around</h3>
              <p className="text-[14px] text-[#6a6a6a] leading-relaxed">{area.transport}</p>
            </div>
            <div className="p-5 rounded-2xl bg-white border border-[#f0ece5]">
              <h3 className="font-display text-[17px] font-500 text-[#2b2b2b] mb-2 flex items-center gap-2"><GraduationCap size={17} className="text-[#b8935f]" /> Schools nearby</h3>
              <ul className="space-y-1">
                {area.schools.map((s) => <li key={s} className="text-[14px] text-[#6a6a6a] flex items-start gap-2"><span className="text-[#b8935f] mt-1">·</span>{s}</li>)}
              </ul>
            </div>
          </section>

          <section className="p-6 rounded-2xl bg-[#1c1c1e] text-white">
            <h2 className="font-display text-[22px] font-400 mb-4 flex items-center gap-2"><TrendingUp size={19} className="text-[#d9b98a]" /> {area.name} property market</h2>
            <div className="space-y-3">
              <div><p className="text-[11px] uppercase tracking-wide text-[#d9b98a] mb-1">Price guide</p><p className="text-[15px] text-white/85 leading-relaxed">{area.priceGuide}</p></div>
              <div><p className="text-[11px] uppercase tracking-wide text-[#d9b98a] mb-1">Investor view</p><p className="text-[15px] text-white/85 leading-relaxed">{area.whyInvest}</p></div>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-[22px] font-500 text-[#2b2b2b] flex items-center gap-2"><PoundSterling size={19} className="text-[#b8935f]" /> Recent sales in {city} {area.postcode}</h2>
              {sold?.sources?.length > 0 && <span className="text-[11px] text-[#a0a0a0]">Source: {sold.sources.join(" · ")}</span>}
            </div>
            {sold === null ? (
              <p className="text-[14px] text-[#8a8a8a] p-4 rounded-xl bg-white border border-[#f0ece5]">Loading live sold-price data…</p>
            ) : (sold.transactions || []).length === 0 ? (
              <p className="text-[14px] text-[#8a8a8a] p-4 rounded-xl bg-white border border-[#f0ece5]">No recent Land Registry sales found for this postcode right now.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-4 rounded-xl bg-white border border-[#f0ece5]" data-testid="sold-median"><p className="text-[11px] uppercase tracking-wide text-[#a0a0a0]">Median sold</p><p className="font-display text-[24px] font-500 text-[#2b2b2b]">{money(sold.median)}</p></div>
                  <div className="p-4 rounded-xl bg-white border border-[#f0ece5]"><p className="text-[11px] uppercase tracking-wide text-[#a0a0a0]">Area average</p><p className="font-display text-[24px] font-500 text-[#2b2b2b]">{money(sold.average)}</p></div>
                </div>
                <div className="rounded-xl bg-white border border-[#f0ece5] overflow-hidden" data-testid="sold-list">
                  {sold.transactions.map((t, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-[#f5f1ea] last:border-0">
                      <div><p className="text-[13px] text-[#2b2b2b]">{t.address}</p><p className="text-[11px] text-[#a0a0a0]">{t.date}</p></div>
                      <span className="text-[14px] font-500 text-[#b8935f]">{money(t.price)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-[#a0a0a0] mt-2">Live HM Land Registry Price Paid data — actual completed sales, not estimates.</p>
              </>
            )}
          </section>
        </article>

        <aside className="lg:sticky lg:top-[90px] self-start space-y-4">
          <div className="p-6 rounded-2xl bg-white border border-[#f0ece5]" data-testid="area-valuation-card">
            <h3 className="font-display text-[19px] font-500 text-[#2b2b2b] mb-1">What's my {area.name} home worth?</h3>
            <p className="text-[13px] text-[#8a8a8a] mb-4">Get an instant guide price for {city} {area.postcode}, powered by live market data.</p>
            {valResult ? (
              <div className="text-center py-3" data-testid="area-val-result">
                <p className="text-[12px] uppercase tracking-wide text-[#a0a0a0]">Estimated value</p>
                <p className="font-display text-[34px] font-500 text-[#b8935f] leading-none my-1">{money(valResult.estimate)}</p>
                <p className="text-[12px] text-[#8a8a8a]">Range {money(valResult.low)}–{money(valResult.high)}</p>
                {valResult.confidence && <p className="text-[11px] text-[#6a6a6a] mt-1 capitalize">{valResult.confidence} confidence{valResult.localSalesAnalysed ? ` · ${valResult.localSalesAnalysed} local sales` : ""}</p>}
                {valResult.marketCalibrated && <p className="text-[11px] text-green-600 mt-0.5">Property-specific estimate from live market data</p>}
                <button onClick={() => setValResult(null)} className="mt-4 text-[13px] text-[#b8935f] underline">New estimate</button>
              </div>
            ) : (
              <form onSubmit={submitVal} className="space-y-2.5" data-testid="area-valuation-form">
                <select value={valForm.propertyType} onChange={(e) => setValForm({ ...valForm, propertyType: e.target.value })} className="w-full h-[42px] px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid="val-type">
                  {["Apartment", "Terraced House", "Semi-Detached House", "Detached House", "Town House"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={valForm.bedrooms} onChange={(e) => setValForm({ ...valForm, bedrooms: e.target.value })} className="w-full h-[42px] px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid="val-beds">
                  {[1, 2, 3, 4, 5].map((b) => <option key={b} value={b}>{`${b} bedroom${b > 1 ? "s" : ""}`}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <select value={valForm.bathrooms} onChange={(e) => setValForm({ ...valForm, bathrooms: e.target.value })} className="w-full h-[42px] px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid="val-baths">
                    {[1, 2, 3, 4, 5].map((b) => <option key={b} value={b}>{`${b} bath${b > 1 ? "s" : ""}`}</option>)}
                  </select>
                  <select value={valForm.finish} onChange={(e) => setValForm({ ...valForm, finish: e.target.value })} className="w-full h-[42px] px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid="val-finish">
                    <option value="renovated">Renovated</option>
                    <option value="average">Average</option>
                    <option value="needs_work">Needs work</option>
                  </select>
                </div>
                <input required placeholder="Your name" value={valForm.name} onChange={(e) => setValForm({ ...valForm, name: e.target.value })} className="w-full h-[42px] px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid="val-name" />
                <input required type="email" placeholder="Email" value={valForm.email} onChange={(e) => setValForm({ ...valForm, email: e.target.value })} className="w-full h-[42px] px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid="val-email" />
                <input placeholder="Phone (optional)" value={valForm.phone} onChange={(e) => setValForm({ ...valForm, phone: e.target.value })} className="w-full h-[42px] px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" data-testid="val-phone" />
                <button type="submit" disabled={valBusy} className="w-full inline-flex items-center justify-center gap-2 h-[46px] rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60" data-testid="val-submit">{valBusy ? <><Loader2 size={15} className="animate-spin" /> Valuing…</> : <><TrendingUp size={15} /> Get instant estimate</>}</button>
              </form>
            )}
            <Link to="/buying" data-testid="area-cta-listings" className="w-full inline-flex items-center justify-center gap-2 h-[44px] rounded-lg border border-[#e2d8c8] text-[#2b2b2b] text-[14px] font-500 hover:border-[#b8935f] hover:text-[#b8935f] transition-colors mt-3"><Home size={16} /> Browse homes in {city}</Link>
          </div>
          {isAdmin && (
            <button onClick={genCampaign} disabled={campBusy} data-testid="area-campaign-btn" className="w-full inline-flex items-center justify-center gap-2 h-[48px] rounded-2xl bg-[#1c1c1e] text-white text-[14px] font-500 hover:bg-black transition-colors disabled:opacity-60">{campBusy ? <><Loader2 size={15} className="animate-spin" /> Generating…</> : <><Megaphone size={16} /> Generate {area.name} campaign</>}</button>
          )}
        </aside>
      </div>

      <div className="max-w-[1080px] mx-auto px-5 md:px-8 pb-16">
        <h2 className="font-display text-[22px] font-500 text-[#2b2b2b] mb-5">Explore nearby areas</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {related.map((a) => (
            <Link key={a.slug} to={`/area-guides/${a.slug}`} data-testid={`related-${a.slug}`} className="group flex items-center gap-4 p-4 rounded-xl bg-white border border-[#f0ece5] hover:border-[#b8935f]/40 transition-colors">
              <img src={a.heroImage} alt={a.name} className="w-16 h-16 rounded-lg object-cover" />
              <div><p className="font-display text-[17px] font-500 text-[#2b2b2b]">{a.name}</p><span className="text-[13px] text-[#b8935f] inline-flex items-center gap-1 group-hover:gap-2 transition-all">{city} {a.postcode} <ArrowRight size={13} /></span></div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AreaGuideDetail;
